'use client';

import React from 'react';
import Link from 'next/link';
import { IHomePage } from '@/.generated';
import { getCSLPAttributes } from '@/utils/type-guards';
import { trackAdobeClick } from '@/utils/adobe/click';
import { navTilesVariants } from './NavTiles.styles';
import { WithMetadata } from '@/lib/types';

// Model of a single nav tile, derived from the CMS schema (element of the tiles tuple)
type Tile = WithMetadata<NonNullable<NonNullable<IHomePage['nav_tiles']>['tiles']>[number]>;

// Renders a single navigation tile with icon and label; fires the Adobe Click event on click
export const NavTile = ({
  icon,
  destination_url,
  open_in_new_tab,
  hide_in_mobile,
  _metadata,
  $,
}: Tile) => {
  const { tile, icon: iconClass, label } = navTilesVariants({ hideInMobile: hide_in_mobile });

  if (!destination_url?.href) return null;

  const linkLabel = destination_url.title;
  const ariaLabel = open_in_new_tab ? `${linkLabel} (Opens in a new tab)` : linkLabel;

  // Track the click with explicit nav-tile location/type
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    trackAdobeClick(
      {
        clickName: linkLabel,
        clickLocation: `Nav-Tiles${_metadata ? `_${_metadata.uid}` : ''}`,
        clickType: 'navtile-link',
      },
      e
    );
  };

  return (
    <Link
      href={destination_url.href}
      className={tile()}
      target={open_in_new_tab ? '_blank' : undefined}
      rel={open_in_new_tab ? 'noopener noreferrer' : undefined}
      aria-label={ariaLabel}
      onClick={handleClick}
      {...getCSLPAttributes($?.destination_url)}
    >
      {icon && <i className={`${icon} ${iconClass()}`} aria-hidden="true" />}
      <span className={label()}>{linkLabel}</span>
    </Link>
  );
};
